import type { AnswerError, Answers, Lang } from '@rx-manager/forms';
import type { DraftAnswers } from '@rx-manager/forms/react';
import type { PublicBookingForm, PublicBookingFormAnswers } from '@rx-manager/shared';

/**
 * Track SFM rev 2 — the forms the online booking asks, one question at a time, after the time and
 * before the patient's details (SFM-D26, D28). Pure, so the rules are tested without a page.
 *
 * Answers live here in memory and nowhere else: never in storage, never in the address (SFM-D35).
 */

export type FormStatus = 'not_started' | 'in_progress' | 'done' | 'skipped';

export interface FormEntry {
  form: PublicBookingForm;
  answers: DraftAnswers;
  status: FormStatus;
  /** What the form's own check accepted at its last "Continue": what the booking sends. */
  checked: Answers | null;
}

export type FormsView =
  | { kind: 'intro' }
  | {
      kind: 'runner';
      /** Bumped to start the runner afresh where it is told to. */
      mount: number;
      startAtReview?: boolean;
      startAt?: string;
      errors?: Record<string, AnswerError>;
    };

export interface BookingFormsState {
  serviceId: string | null;
  load: 'idle' | 'loading' | 'ready' | 'failed';
  entries: FormEntry[];
  /** The server could not take answers just now: the booking goes on without them. */
  unavailable: boolean;
  cursor: number;
  view: FormsView;
  /** Opened from the booking's review: finishing the form goes back there. */
  returnTo: 'review' | null;
}

export const NO_FORMS: BookingFormsState = {
  serviceId: null,
  load: 'idle',
  entries: [],
  unavailable: false,
  cursor: 0,
  view: { kind: 'intro' },
  returnTo: null,
};

export type FormsAction =
  | { type: 'load_start'; serviceId: string }
  | { type: 'loaded'; serviceId: string; forms: PublicBookingForm[] }
  | { type: 'load_failed'; serviceId: string }
  | { type: 'answers'; answers: DraftAnswers }
  | { type: 'finish'; checked: Answers }
  | { type: 'skip' }
  | { type: 'open'; index: number; view?: FormsView; returnTo?: 'review' | null }
  | { type: 'returned' }
  | { type: 'unavailable' }
  | { type: 'reset' };

/** A form that lets the booking go on: answered, or an optional one skipped. */
export function isSettled(entry: FormEntry): boolean {
  return entry.status === 'done' || (entry.status === 'skipped' && !entry.form.required);
}

/** The booking asks questions for this service (and can take them). */
export function hasForms(state: BookingFormsState): boolean {
  return !state.unavailable && state.load === 'ready' && state.entries.length > 0;
}

export function allSettled(state: BookingFormsState): boolean {
  return state.entries.every(isSettled);
}

/** The first form still to answer (or decide on), or -1. */
export function firstUnsettled(state: BookingFormsState): number {
  return state.entries.findIndex((entry) => !isSettled(entry));
}

/** How a form opens: an optional one not answered yet on its intro; one answered on its review. */
export function viewFor(entry: FormEntry, mount: number): FormsView {
  if (!entry.form.required && (entry.status === 'not_started' || entry.status === 'skipped')) {
    return { kind: 'intro' };
  }
  return { kind: 'runner', mount, startAtReview: entry.status === 'done' };
}

/** The form's language for this page: the page's, when the form has it. */
export function formLang(form: PublicBookingForm, pageLang: Lang): Lang {
  return form.languages.includes(pageLang) ? pageLang : 'en';
}

/** What Confirm sends: forms answered, never skipped ones, and never an empty list. */
export function confirmForms(
  state: BookingFormsState,
  pageLang: Lang,
): PublicBookingFormAnswers[] | undefined {
  if (!hasForms(state)) return undefined;
  const sent = state.entries
    .filter((entry) => entry.status === 'done' && entry.checked)
    .map((entry) => ({
      formId: entry.form.formId,
      version: entry.form.version,
      language: formLang(entry.form, pageLang),
      answers: entry.checked!,
    }));
  return sent.length > 0 ? sent : undefined;
}

const entryOf = (form: PublicBookingForm): FormEntry => ({
  form,
  answers: {},
  status: 'not_started',
  checked: null,
});

const mountOf = (view: FormsView): number => (view.kind === 'runner' ? view.mount : 0);

export function formsReducer(state: BookingFormsState, action: FormsAction): BookingFormsState {
  switch (action.type) {
    case 'reset':
      return NO_FORMS;
    case 'load_start':
      // Another service: nothing of the last one's answers comes along.
      return state.serviceId === action.serviceId
        ? { ...state, load: 'loading' }
        : { ...NO_FORMS, serviceId: action.serviceId, load: 'loading' };
    case 'load_failed':
      return state.serviceId === action.serviceId ? { ...state, load: 'failed' } : state;
    case 'loaded': {
      if (state.serviceId !== action.serviceId) return state;
      // A reload (the service's forms changed) keeps what was answered: a form already answered
      // keeps its version, which the server takes (SFM-D31). New ones join; gone ones go.
      const kept = new Map(state.entries.map((entry) => [entry.form.formId, entry]));
      const entries = action.forms.map((form) => {
        const before = kept.get(form.formId);
        if (!before) return entryOf(form);
        return before.status === 'not_started'
          ? { ...before, form }
          : { ...before, form: { ...before.form, required: form.required } };
      });
      return {
        ...state,
        load: 'ready',
        entries,
        cursor: Math.min(state.cursor, Math.max(entries.length - 1, 0)),
      };
    }
    case 'answers': {
      const entries = state.entries.map((entry, index) =>
        index === state.cursor
          ? {
              ...entry,
              answers: action.answers,
              // A change to an answered form must be finished again.
              status: 'in_progress' as const,
            }
          : entry,
      );
      return { ...state, entries };
    }
    case 'finish': {
      const entries = state.entries.map((entry, index) =>
        index === state.cursor ? { ...entry, status: 'done' as const, checked: action.checked } : entry,
      );
      return { ...state, entries };
    }
    case 'skip': {
      const entries = state.entries.map((entry, index) =>
        index === state.cursor && !entry.form.required
          ? { ...entry, status: 'skipped' as const, checked: null }
          : entry,
      );
      return { ...state, entries };
    }
    case 'open': {
      const entry = state.entries[action.index];
      if (!entry) return state;
      const mount = mountOf(state.view) + 1;
      return {
        ...state,
        cursor: action.index,
        view: action.view
          ? action.view.kind === 'runner'
            ? { ...action.view, mount }
            : action.view
          : viewFor(entry, mount),
        returnTo: action.returnTo === undefined ? state.returnTo : action.returnTo,
      };
    }
    case 'returned':
      return { ...state, returnTo: null };
    case 'unavailable':
      return { ...state, unavailable: true };
  }
}
